<template>
	<div
		class="options-modal-item"
		:class="{'active': isSelected, 'has-rules': notAvailable.length}"
		:name="`myTrigger-${cardId}`"
		@click="selectOption"
	>
		<template v-if="addCustomProduct">
			<!-- add custom product -->
			<div class="options-modal-item add-custom-product" v-if="!cardDetails" @click="triggerModal('add', null)">
				<md-button id="add-item-btn" class="md-primary md-button-large touchless">
					<base-icon icon-name="add" />
					<span>Add your own</span>
					<span>Product</span>
				</md-button>
			</div>
			<!-- display custom product -->
			<div class="options-modal-item-content custom-product" v-else>
				<div class="options-modal-item-content-top">
					<h6 class="mobile-subtitle-2">{{ card.customContent.title }}</h6>
					<p class="body-1">{{ card.customContent.desc }}</p>
				</div>
				<div class="options-modal-item-content-bottom">
					<md-button
						class="md-border-primary md-primary has-icon edit-btn"
						@click.prevent="triggerModal('edit', card.customContent.desc)"
					>
						<base-icon :icon-color="'#1562AD'" icon-name="pen" />
						<span>Edit</span>
					</md-button>
					<label
						class="checkbox mobile-hide"
						@click.stop="compareOptions"
						title="We are currently working on this feature. By clicking here you will see an overview of different options"
						v-tippy="{trigger: 'click', theme: 'dark', animation: 'shift-away', zIndex: '999999'}"
					>
						<input type="checkbox" disabled />
						<span class="checkmark"></span>
						Compare
					</label>
				</div>
			</div>
		</template>

		<template v-else>
			<div class="options-modal-item-img touchless" :style="{backgroundImage: `url(${getBgImg})`}"></div>
			<div class="options-modal-item-content">
				<div class="options-modal-item-content-top">
					<span class="overline touchless">Cat No. {{ card.catalogNumber }}</span>
					<h6 class="mobile-subtitle-2" v-html="card.name[getLang]" />
					<div class="card-desc" ref="descParent">
						<p class="body-1 mobile-hide" v-html="getCardDesc" ref="desc"></p>
					</div>
				</div>
				<div class="options-modal-item-content-bottom">
					<div class="options-modal-item-sizes mobile-hide" v-if="card.options">
						<button class="button-small options-modal-item-size">100</button>
						<button class="button-small options-modal-item-size">500</button>
						<button class="button-small options-modal-item-size">2500</button>
					</div>
					<!-- priceStatuses start -->
					<!-- Request Demo -->
					<p v-if="card.itemType == 'Instrument' && !isGeneGlobe">
						<md-button class="md-primary md-border-primary request-demo" @click="$emit('requestDemo')">
							Request a demo
						</md-button>
					</p>
					<!-- ComingSoon -->
					<div
						class="options-modal-item-price touchless"
						v-if="getPriceStatus == 'ComingSoon' && !isGeneGlobe"
					>
						<p class="h6">Coming Soon</p>
					</div>
					<!-- CannotOrderOnline -->
					<div v-else-if="getPriceStatus == 'CannotOrderOnline' && !isGeneGlobe">
						<div class="caption-2" v-if="optionCardPrice && optionCardPrice.value">
							<h6 data-cy="product-price">
								<span v-html="getLocalizedPriceStr(optionCardPrice.value, optionCardPrice.currencyIsoCode)"></span>
							</h6>
						</div>
					</div>
					<!-- Login -->
					<div v-else-if="getPriceStatus == 'Login' && !isGeneGlobe">
						<div v-if="!getLoggedInStatus">
							<p class="caption">
								<a href="#" @click.prevent="$emit('logInRequested', card.key)">Log in</a>
								to see pricing
							</p>
						</div>
						<div
							class="options-modal-item-price touchless"
							v-else-if="getLoggedInStatus && optionCardPrice && optionCardPrice.value"
						>
							<p v-if="isDiscounted" class="h6 custom-price" data-cy="product-price">
								<span v-html="getLocalizedPriceStr(optionCardPrice.custom, optionCardPrice.currencyIsoCode)"></span>
							</p>
							<p :class="[isDiscounted ? 'caption-2' : 'h6']">
								<span v-if="isDiscounted" data-cy="product-discount"
									>{{ optionCardPrice.value | getPercentDiff(optionCardPrice.custom) }}% off
								</span>
								<span
									:class="{'strike-through': isDiscounted}"
									v-html="getLocalizedPriceStr(conditionalPrice, conditionalCurrencyIsoCode)"
									data-cy="product-price-before-discount"
								>
								</span>
							</p>
						</div>
					</div>
					<!-- Display -->
					<div
						class="options-modal-item-price touchless"
						v-else-if="
							getPriceStatus == 'Display' && optionCardPrice && optionCardPrice.value && !isGeneGlobe
						"
					>
						<p
							v-if="isDiscounted"
							class="h6 custom-price"
							v-html="getLocalizedPriceStr(customerPrice.amount, customerPrice.currencyIsoCode)"
							data-cy="product-price"
						></p>
						<p :class="[isDiscounted ? 'caption-2' : 'h6']">
							<span v-if="isDiscounted" data-cy="product-discount"
								>{{ optionCardPrice.value | getPercentDiff(customerPrice.amount) }}% off
							</span>
							<span
								:class="{'strike-through': isDiscounted}"
								v-html="getLocalizedPriceStr(conditionalPrice, conditionalCurrencyIsoCode)"
								data-cy="product-price-before-discount"
							>
							</span>
						</p>
					</div>
					<!-- Inquire -->
					<div v-if="showInquireButton && !isGeneGlobe" class="inquire-btn">
						<p>
							<md-button
								class="md-primary md-border-primary"
								@click="$emit('sendToInquireLink', cardDetails.card.itemType)"
							>
								Inquire
							</md-button>
						</p>
					</div>
					<!-- priceStatuses end -->
					<!-- Gene Globes -->
					<div class="modal-gg-cta" v-if="card.itemType == 'GeneGlobe'">
						<div class="modal-gg-cta-caption caption">
							Configure and purchase this product on GeneGlobe.
						</div>
						<md-button
							class="md-primary md-border-primary"
							@click="$emit('sendToGeneGlobeLink', card.articleNumber)"
						>
							<span class="caption-2" style="white-space: normal">Open GeneGlobe</span>
							<base-icon icon-name="arrow-top-right" />
						</md-button>
					</div>

					<!-- Compare -->
					<!--
                        *** REMOVING UNTIL WE ARE READY FOR THIS FEATURE ***
                        <label
						v-show="false"
						class="checkbox mobile-hide"
						@click.stop="compareOptions"
						title="We are currently working on this feature. By clicking here you will see an overview of different options"
						v-tippy="{trigger: 'click', theme: 'dark', animation: 'shift-away', zIndex: '999999'}"
					>
						<input type="checkbox" disabled />
						<span class="checkmark"></span>
						Compare
					</label>

                    -->

					<!-- Product Groups -->
					<template v-if="showProductGroups">
						<ProductGroups
							:parentSelected="isSelected"
							v-if="prodGroup.selected"
							:currentProduct="cardId"
							:productsInGroup="getProductGroups"
							:productGroupsState="productGroupsState"
							@extendProductGroups="$emit('extendProductGroups')"
							@chipSelected="chipSelected"
						/>
					</template>
				</div>
			</div>
			<!-- Tool Tip -->
			<div v-if="notAvailable.length" class="rule-tool-tip" :ref="`tooltip-${cardId}`"></div>
			<tippy
				v-if="notAvailable.length"
				:to="`myTrigger-${cardId}`"
				:appendTo="addTippyTo"
				:key="productIndex"
				placement="bottom-start"
				boundary="viewport"
				distance="0"
				sticky="true"
				flip="false"
			>
				<div class="caption-small"><span>Not available when selected:</span></div>
				<ul>
					<li
						v-for="i in 5"
						v-if="notAvailable[i - 1]"
						v-html="notAvailable[i - 1]"
						:key="i"
						class="caption-small"
					></li>
				</ul>
				<div v-if="notAvailable.length > 5" class="caption-small"><span>...</span></div>
				<div class="caption-small"><span>Deselect these products from the experiment to make the product available.</span></div>
			</tippy>
		</template>
	</div>
</template>

<script>
/* eslint-disable vue/no-async-in-computed-properties */
import throttle from 'lodash.throttle';
import priceFormatter from '@/mixins/priceFormatter';
import {trackEvent} from '@/services/tracking.js';
import ProductGroups from './ProductGroups.vue';

export default {
	name: 'OptionsCard',
	mixins: [priceFormatter],
	components: {
		ProductGroups,
	},
	props: {
		cardDetails: {
			type: Object,
		},
		addCustomProduct: {
			type: Boolean,
		},
		country: {
			type: String,
			default: 'US',
		},
		stepName: {
			type: String,
		},
		productIndex: {
			type: Number,
		},
		selectedProduct: {
			type: String,
		},
		isDisabled: {
			type: Boolean,
		},
		productGroupsState: {
			type: Boolean,
		},
		showProductGroups: {
			type: Boolean,
			default: true,
		},
	},
	data() {
		return {
			openModal: false,
			toolTipList: [],
			prodGroup: {
				selected: null,
			},
			desc: {
				lineHeight: null,
				fontSize: null,
				CPL: null,
			},
			customerPrice: null,
		};
	},
	methods: {
		getLocalizedPriceStr(price, currency) {
			return "<span translate='no'>" + this.formatPrice(price, currency) + '</span>';
		},
		compareOptions() {
			return trackEvent('ctaClick', {eVar58: 'Compare | Workflow Configurator'});
			// will add selections to compare
		},
		getPrice(prices) {
			if (!prices?.length) return;
			// return flitered for price based on country
			return prices.filter((price) => price.countryIsoCode === this.country)[0].price;
		},
		triggerModal(task, value) {
			const key = this.addCustomProduct ? this.generateKey : this.cardId;
			const stepName = this.stepName;
			const productIndex = this.productIndex;
			this.$emit('showCustomProductModal', {task, value, key, stepName, productIndex});
		},
		addTippyTo() {
			return this.$refs[`tooltip-${this.cardId}`];
		},
		async createToolTipList() {
			if (!this.card?.rules || !this.isDisabled) return;
			for (const id of this.card.rules.excludeBy) {
				if (!id) continue;
				const name = await this.$store.getters['workflow/productName'](id);
				this.toolTipList.push(name);
			}
			for (const id of this.card.rules.exclusiveBy) {
				if (!id) continue;
				const name = await this.$store.getters['workflow/productName'](id);
				this.toolTipList.push(name);
			}
		},
		chipSelected(key) {
			this.productGroupsInit(this.cardDetails.card, key);
		},
		selectOption() {
			this.$emit('selectOption', this.cardId);
			this.$emit('onCardSelected', this.cardDetails);
		},
		productGroupsInit(card, selected = this.selectedProduct) {
			if (!card) return;
			const selectedCard = card.find((product) => product.key == selected) || card[0];
			this.prodGroup.selected = selectedCard;
		},
		checkNumberOfLinesInDesc() {
			if (!this.$refs?.descParent) return;
			this.desc.CPL = parseInt((this.$refs.descParent.offsetWidth * 1.6) / this.desc.fontSize) * 3;
		},
		addListeners() {
			window.addEventListener('resize', throttle(this.checkNumberOfLinesInDesc, 300));
		},
		removeListeners() {
			window.removeEventListener('resize', throttle(this.checkNumberOfLinesInDesc, 300));
		},
		cacheDescStyles() {
			const el = this.$refs.desc;
			if (!el) return;
			const styles = window.getComputedStyle(el);
			this.desc.lineHeight = +styles.getPropertyValue('line-height').replace('px', '');
			this.desc.fontSize = +styles.getPropertyValue('font-size').replace('px', '');
			this.checkNumberOfLinesInDesc();
		},
	},
	computed: {
		cardId() {
			return this.card?.key;
		},
		generateKey() {
			const wfs = this.$store.getters['workflow/wfStep'](this.stepName);
			const selectedProducts = wfs.selected.map((product) => product?.initial);
			let i = 0;
			while (selectedProducts.includes(`custom-product-${i}`)) i++;

			return `custom-product-${i}`;
		},
		isSelected() {
			return this.selectedProduct === this.cardId;
		},
		isDiscounted() {
			return this.customerPrice ? this.customerPrice.amount < this.optionCardPrice.value : false;
		},
		conditionalPrice() {
			return this.isDiscounted
				? this.optionCardPrice.value
				: this.customerPrice
				? this.customerPrice.amount
				: this.optionCardPrice.value;
		},		
		conditionalCurrencyIsoCode() {
			return this.isDiscounted
				? this.optionCardPrice.currencyIsoCode
				: this.customerPrice
				? this.customerPrice.currencyIsoCode
				: this.optionCardPrice.currencyIsoCode;
		},
		card() {
			if (!this.cardDetails) return;
			return this.prodGroup.selected ? this.prodGroup.selected : this.cardDetails.card;
		},
		getLang() {
			return this.$store.state.LANG;
		},
		optionCardPrice() {
			if (!this.card) return;
			return this.getPrice(this.card.prices);
		},
		getBgImg() {
			if (!this.card || this.card.customContent) return;

			const imageUrl = this.card.length ? this.card[0].mobileImage : this.card.mobileImage;
			return imageUrl.replace(/ /g, '%20');
		},
		getLoggedInStatus() {
			const jwt = this.$store.state.jwt;
			if (jwt) return `${jwt.lastName}, ${jwt.firstName}`;
			return null;
		},
		getPriceStatus() {
			return this.card.priceStatuses[this.country] ? this.card.priceStatuses[this.country] : 'NA';
		},
		showInquireButton() {
			const priceStatus = this.getPriceStatus;
			if (priceStatus == 'NA') return true;

			const isLoggedIn = !!this.getLoggedInStatus;
			const hasPrice = !!(this.optionCardPrice && this.optionCardPrice.value);
			if (
				priceStatus == 'Inquire' ||
				(priceStatus == 'Login' && isLoggedIn && !hasPrice) ||
				(priceStatus == 'Display' && !hasPrice) ||
				(priceStatus == 'CannotOrderOnline' && !hasPrice)
			)
				return true;
			return false;
		},
		isGeneGlobe() {
			return this.card.itemType == 'GeneGlobe';
		},
		notAvailable() {
			return this.toolTipList;
		},
		getProductGroups() {
			if (!this.prodGroup.selected) return;
			return Array.from(this.cardDetails.card, ({key, catalogNumber, productConfigurator}) => ({
				key,
				catalogNumber,
				productConfigurator,
			})).sort((a, b) => a.catalogNumber - b.catalogNumber);
		},
		getCardDesc() {
			if (!this.getCPL) return;
			const desc = this.card.description;
			const link = this.card.pageLink
				? `<a href="${this.card.pageLink}" class="product-link mobile-hide" target="_blank" rel="noopener noreferrer"> see product details</a>`
				: '';
			return desc.length > this.getCPL
				? `<div class="desc">${desc.substring(0, this.getCPL)}</div> ... ${link}`
				: `<div class="desc">${desc}</div>  ${link}`;
		},
		getCPL() {
			return this.desc.CPL;
		},
		findCustomerPrice() {
			if (this.card) {
				return this.$store.getters['customerPrices/priceFor'](this.card.catalogNumber);
			}
		},
	},
	watch: {
		selectedProduct(val) {
			if (this.cardDetails?.card?.length) this.productGroupsInit(this.cardDetails?.card, val);
		},
		findCustomerPrice: {
			handler(newValue) {
				this.customerPrice = newValue;
			},
			immediate: true,
		},
	},
	created() {
		if (this.cardDetails?.card?.length) this.productGroupsInit(this.cardDetails.card, this.selectedProduct);
	},
	mounted() {
		this.createToolTipList();
		this.cacheDescStyles();
		this.addListeners();
	},
	beforeDestroyed() {
		this.removeListeners();
	},
};
</script>

<style lang="scss">
.options-modal-item {
	box-shadow: none;
	cursor: pointer;
	background: $white;
	display: flex;
	@include box(100%, auto);
	border: 1px solid $color-extra-light-blue;
	border-radius: 8px;
	overflow: hidden;
	min-height: 130px;
	transition: box-shadow 0.2s, background 0.2s;
	@media (min-width: $mobile + 1) {
		min-height: 540px;
		@include flexColumn;
	}
	max-width: 540px;
	&:hover {
		box-shadow: 0px 8px 16px rgba(8, 35, 48, 0.2);
	}

	.touchless {
		pointer-events: none;
	}
	&.add-custom-product {
		height: 100%;
		justify-content: center;
		align-items: center;
		cursor: pointer;
		max-width: 100%;
		button {
			width: 190px;
			padding: 0;
			.plus {
				display: inline-block;
				padding-bottom: 1.3rem;
			}
			.icons-add {
				font-size: 64px;
			}
			@media (max-width: $mobile) {
				&#add-item-btn {
					.md-ripple {
						height: auto;
						.md-button-content {
							@include textStyles(16, 23, 0.4);
							.icons-add {
								font-size: 40px;
							}
						}
					}
				}
			}
		}
	}

	&.active {
		background: $color-super-light-blue;
		transition: box-shadow 0.2s;
		transition: background 0.2s;
		box-shadow: 0px 0px 0px 2px $color-blue;
	}
	&-img {
		width: 100px;
		max-width: 20%;
		background-repeat: no-repeat;
		background-position: center;
		background-size: cover;
		@media (min-width: $mobile +1) {
			width: 100%;
			max-width: 100%;
			height: 164px;
		}
	}
	&-content {
		@include flexColumn;
		justify-content: space-between;
		flex-grow: 1;
		padding: 10px 16px;
		@media (min-width: $mobile + 1) {
			padding: 1rem 1.1rem;
		}
		&.custom-product {
			justify-content: flex-start;
			p {
				white-space: pre-wrap;
			}
			button.edit-btn {
				margin-bottom: 10px !important;
				.md-ripple {
					padding: 4px 8.02px !important;
					.md-button-content {
						justify-content: space-between;
						line-height: 1;
					}
				}
			}
		}
		&-top {
			span {
				color: $color-grey;
				display: block;
			}
			h6 {
				display: inline;
				margin: 0 0 0.6rem;
				color: $color-blue;
			}
			.product-link {
				color: $color-blue !important;
				margin-bottom: 10px;

				a:hover,
				:focus,
				:visited,
				:active {
					color: $color-blue !important;
				}
			}
			.card-desc {
				position: relative;
				overflow: hidden;
				margin: 9.6px 0 16px;
				.desc {
					display: initial;
				}
				p {
					margin: 0;
					color: $color-text-grey;
					a {
						white-space: nowrap;
					}
				}
			}
		}
		&-bottom {
			flex-grow: 1;
			.modal-gg-cta {
				@include flexColumn;
				align-items: flex-start;
				padding: 0px 8px 8px 8px;
				border-radius: 4px;
				background: $color-extra-light-yellow;
				margin-bottom: 16px;
				&-caption {
					color: $color-text-grey;
					margin: 8px 0px;
					flex: none;
					align-self: flex-start;
				}
				.md-button {
					padding: 0px 1px;
					.caption-2 {
						letter-spacing: 0.4px;
						margin-right: 8px;
					}
				}
			}
			@media (max-width: $mobile) {
				margin-top: 0.5rem;
				.modal-gg-cta {
					margin-bottom: 0;
				}
				.md-button {
					width: 100%;
					max-width: 296px;
				}
			}
		}
	}
	&-price {
		.h6,
		.caption-2 {
			color: $black;
		}
		.h6 {
			&.custom-price {
				color: $color-red;
			}
			@media (max-width: $mobile) {
				margin: 0;
			}
		}
		.strike-through {
			text-decoration: line-through;
		}
	}
	.checkbox {
		max-width: 100px;
	}

	// rules applied
	&.has-rules {
		cursor: not-allowed;
		.options-modal-item-img,
		.options-modal-item-content {
			opacity: 0.6;
			pointer-events: none;
		}
	}
	.rule-tool-tip {
		div {
			text-align: left;
		}
		ul {
			padding-left: 24px;
			text-align: left;
			margin: 0;
			li {
				padding: 1px 0;
				br {
					display: none;
				}
			}
		}
	}
}
.add-custom-product {
	.md-button-content {
		flex-direction: column;
	}
}
.options-card-mobile {
	@media (max-width: $mobile) {
		flex-direction: column;
		.options-modal-item-img {
			height: 200px;
			width: 100%;
			max-width: unset;
		}
		.options-modal-item-content-bottom .md-button {
			max-width: 100%;
		}
	}
}
</style>
